import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getNews, getAnnouncements } from "../../services/api";
import type { Actualite, Announcement } from "../../types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faNewspaper,
  faArrowLeft,
  faArrowRight,
  faShareAlt,
  faLink,
  faBullhorn,
  faSearch,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookF,
  faTwitter,
  faLinkedinIn,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";
import { useLanguage } from "../../context/LanguageContext";
import { resolveMediaUrl } from "../../utils/media";

const NewsDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();
  const [news, setNews] = useState<Actualite | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedNews, setRelatedNews] = useState<Actualite[]>([]);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (id) {
      fetchNewsDetail();
      fetchAnnouncements();
    }
  }, [id]);

  const fetchNewsDetail = async () => {
    try {
      setLoading(true);
      const response = await getNews();
      const newsItem = response.data.find(
        (item: Actualite) => item.id === parseInt(id!)
      );

      if (newsItem) {
        setNews(newsItem);

        // Get related news (last 3 news except current one)
        const related = response.data
          .filter((item: Actualite) => item.id !== parseInt(id!))
          .slice(0, 3);
        setRelatedNews(related);
      }
    } catch (error) {
      console.error("Error fetching news detail:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      setAnnouncementsLoading(true);
      const response = await getAnnouncements();
      setAnnouncements(response.data.slice(0, 5)); // Last 5 announcements
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  // Clean HTML tags from text
  const cleanHtmlTags = (text: string) => {
    if (!text) return "";
    return text
      .replace(/<p><p>/g, "")
      .replace(/<p>/g, "")
      .replace(/<\/p>/g, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/&nbsp;/g, " ")
      .replace(/<[^>]*>/g, "")
      .trim();
  };

  // Get appropriate content based on language
  const getNewsTitle = () => {
    if (!news) return "";
    return language === "ar"
      ? news.titrearabe || news.titrefrancais
      : news.titrefrancais || news.titrearabe;
  };

  const getNewsText = () => {
    if (!news) return "";
    return language === "ar"
      ? news.textarabe || news.textfrancais
      : news.textfrancais || news.textarabe;
  };

  const getRelatedNewsTitle = (relatedNews: Actualite) => {
    return language === "ar"
      ? relatedNews.titrearabe || relatedNews.titrefrancais
      : relatedNews.titrefrancais || relatedNews.titrearabe;
  };

  const getAnnouncementTitle = (announcement: Announcement) => {
    return language === "ar"
      ? announcement.titrearabe
      : announcement.titrefrancais || announcement.titrearabe;
  };

  // Get YouTube video ID from URL
  const getYouTubeId = (url: string | undefined): string | null => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const getVideoId = () => {
    if (!news) return null;
    const link = language === "ar" ? news.lienar : news.lienfr;
    return getYouTubeId(link);
  };

  // Enhanced function to render content with YouTube videos
  const renderContentWithVideos = (text: string) => {
    if (!text) return null;

    // First clean the HTML tags
    const cleanedText = cleanHtmlTags(text);

    // Split text by lines to handle paragraphs
    const lines = cleanedText.split("\n");

    // Regex for YouTube URLs
    const youtubeRegex =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([\w-]+)(?:[?&]\S*)?/g;

    return lines.map((line, index) => {
      // Skip empty lines
      if (!line.trim()) return <br key={index} />;

      const parts = [];
      let lastIndex = 0;
      let match;

      // Reset regex index for each line
      youtubeRegex.lastIndex = 0;

      let hasMatch = false;

      while ((match = youtubeRegex.exec(line)) !== null) {
        hasMatch = true;
        // Text before match
        if (match.index > lastIndex) {
          parts.push(line.substring(lastIndex, match.index));
        }

        // The Video ID
        const videoId = match[1];

        // Push the video component
        parts.push(
          <div
            key={`video-${index}-${match.index}`}
            className="youtube-embed my-6 w-full mx-auto"
          >
            <div className="relative h-0 pb-[56.25%] overflow-hidden rounded-lg shadow-lg">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                title={`YouTube video ${videoId}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>
        );

        lastIndex = match.index + match[0].length;
      }

      // Text after last match
      if (lastIndex < line.length) {
        parts.push(line.substring(lastIndex));
      }

      // If we found videos, return the mixed content
      if (hasMatch) {
        return (
          <div key={index} className="mb-4">
            {parts.map((part, i) =>
              typeof part === "string" ? <span key={i}>{part}</span> : part
            )}
          </div>
        );
      }

      // No video found, just return text paragraph
      return (
        <p key={index} className="mb-4 text-gray-700 leading-relaxed text-lg">
          {line}
        </p>
      );
    });
  };

  // Share on social media
  const shareOnSocialMedia = (platform: string) => {
    const shareUrl = window.location.href;
    const title = getNewsTitle();
    const text = cleanHtmlTags(getNewsText()).substring(0, 100) + "...";

    let shareLink = "";

    switch (platform) {
      case "facebook":
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          shareUrl
        )}`;
        break;
      case "twitter":
        shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          title
        )}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case "linkedin":
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          shareUrl
        )}`;
        break;
      case "whatsapp":
        shareLink = `https://wa.me/?text=${encodeURIComponent(
          title + " " + shareUrl
        )}`;
        break;
      default:
        return;
    }

    window.open(shareLink, "_blank", "width=600,height=400");
  };

  // Copy link
  const copyLink = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        alert(
          language === "ar" ? "تم نسخ الرابط بنجاح!" : "Lien copié avec succès!"
        );
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  // Format date function
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(
      language === "ar" ? "ar-MA" : "fr-FR",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
      }
    );
  };

  // Direction and text alignment
  const getDirectionClass = () => (language === "ar" ? "rtl" : "ltr");
  const getTextAlignClass = () =>
    language === "ar" ? "text-right" : "text-left";

  // Translations
  const translations = {
    // Loading States
    loadingNews:
      language === "ar"
        ? "جاري تحميل الخبر..."
        : "Chargement de l'actualité...",
    newsNotFound:
      language === "ar" ? "الخبر غير موجود" : "Actualité non trouvée",
    backToNews:
      language === "ar" ? "العودة إلى قائمة الأخبار" : "Retour aux actualités",

    // Hero Section
    ministryNews:
      language === "ar" ? "أخبار الوزارة" : "Actualités du Ministère",
    home: language === "ar" ? "الرئيسية" : "Accueil",
    news: language === "ar" ? "الأخبار" : "Actualités",
    newsDetails: language === "ar" ? "تفاصيل الخبر" : "Détails de l'actualité",

    // News Content
    newsLabel: language === "ar" ? "خبر" : "Actualité",
    undefinedDate:
      language === "ar" ? "غير محدد التاريخ" : "Date non spécifiée",

    // Share Options
    share: language === "ar" ? "مشاركة" : "Partager",
    facebookShare:
      language === "ar" ? "مشاركة على فيسبوك" : "Partager sur Facebook",
    twitterShare:
      language === "ar" ? "مشاركة على تويتر" : "Partager sur Twitter",
    linkedinShare:
      language === "ar" ? "مشاركة على لينكد إن" : "Partager sur LinkedIn",
    whatsappShare:
      language === "ar" ? "مشاركة على واتساب" : "Partager sur WhatsApp",
    copyLink: language === "ar" ? "نسخ الرابط" : "Copier le lien",
    linkCopied:
      language === "ar" ? "تم نسخ الرابط بنجاح!" : "Lien copié avec succès!",

    // Related News
    relatedNews: language === "ar" ? "أخبار ذات صلة" : "Actualités connexes",
    readMore: language === "ar" ? "قراءة المزيد" : "Lire la suite",

    // Sidebar
    searchPlaceholder:
      language === "ar"
        ? "ابحث في الأخبار..."
        : "Rechercher dans les actualités...",
    lastAnnouncements:
      language === "ar" ? "آخر الإعلانات" : "Dernières Annonces",
    viewAll: language === "ar" ? "عرض الكل" : "Voir tout",
    noAnnouncements: language === "ar" ? "لا توجد إعلانات" : "Aucune annonce",
    viewAllAnnouncements:
      language === "ar" ? "عرض جميع الإعلانات" : "Voir toutes les annonces",
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center py-16">
          <FontAwesomeIcon
            icon={faSpinner}
            className="text-mauritania-green-dark text-4xl animate-spin mb-4"
          />
          <p className="text-gray-600">{translations.loadingNews}</p>
        </div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-mauritania-green-dark mb-4">
            {translations.newsNotFound}
          </h2>
          <Link
            to="/news"
            className="text-mauritania-green hover:text-mauritania-green-dark flex items-center justify-center"
          >
            {language === "ar" ? (
              <>
                {translations.backToNews}
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                {translations.backToNews}
              </>
            )}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${getDirectionClass()}`}>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-mauritania-green-dark to-mauritania-green text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div
            className={`flex items-center justify-center mb-4 ${
              language === "ar" ? "flex-row" : "flex-row-reverse"
            }`}
          >
            <FontAwesomeIcon
              icon={faNewspaper}
              className="text-white text-4xl mx-3"
            />
            <h1 className="text-4xl font-bold">{translations.ministryNews}</h1>
          </div>
          <nav
            className={`text-sm flex justify-center items-center ${
              language === "ar" ? "flex-row" : "flex-row-reverse"
            }`}
          >
            <Link to="/" className="hover:underline flex items-center">
              {translations.home}
            </Link>
            <span className="mx-2">/</span>
            <Link to="/news" className="hover:underline">
              {translations.news}
            </Link>
            <span className="mx-2">/</span>
            <span>{translations.newsDetails}</span>
          </nav>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content - News Detail */}
          <div className="lg:w-3/4">
            <article className="bg-white rounded-lg shadow-xl overflow-hidden">
              {/* News Image */}
              <div className="relative h-96 overflow-hidden">
                <img
                  src={
                    resolveMediaUrl(news.image) ||
                    "https://images.unsplash.com/photo-1581093450020-7c01c5c81a2b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                  }
                  alt={getNewsTitle()}
                  className="w-full h-full object-cover"
                />
                <div
                  className={`absolute top-4 ${
                    language === "ar" ? "right-4" : "left-4"
                  } bg-mauritania-gold text-mauritania-green-dark font-semibold py-1 px-3 rounded-full text-sm`}
                >
                  {translations.newsLabel}
                </div>
              </div>

              {/* News Content */}
              <div className="p-8">
                <div
                  className={`flex items-center text-mauritania-gold font-semibold mb-4 ${getTextAlignClass()}`}
                >
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className={language === "ar" ? "mr-2" : "ml-2"}
                  />
                  {news.datepublication
                    ? formatDate(news.datepublication)
                    : translations.undefinedDate}
                </div>

                <h1
                  className={`text-3xl font-bold text-mauritania-green-dark mb-6 ${getTextAlignClass()}`}
                >
                  {getNewsTitle()}
                </h1>

                {/* Video from Link */}
                {getVideoId() && (
                  <div className="mb-8">
                    <div className="relative h-0 pb-[56.25%] overflow-hidden rounded-lg shadow-lg">
                      <iframe
                        src={`https://www.youtube.com/embed/${getVideoId()}`}
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute top-0 left-0 w-full h-full"
                      />
                    </div>
                  </div>
                )}

                <div className="prose max-w-none mb-8">
                  <div
                    className={`text-gray-700 leading-relaxed text-lg whitespace-pre-line ${getTextAlignClass()}`}
                  >
                    {renderContentWithVideos(getNewsText())}
                  </div>
                </div>

                {/* Share Options */}
                <div className="border-t pt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                  <div className="mb-4 sm:mb-0 relative">
                    <button
                      onClick={() => setShowShareOptions(!showShareOptions)}
                      className={`bg-mauritania-gold text-mauritania-green-dark py-2 px-4 rounded-md hover:bg-yellow-600 transition duration-300 flex items-center ${
                        language === "ar" ? "flex-row" : "flex-row-reverse"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={faShareAlt}
                        className={language === "ar" ? "mr-2" : "ml-2"}
                      />
                      {translations.share}
                    </button>

                    {showShareOptions && (
                      <div
                        className={`absolute ${
                          language === "ar"
                            ? "left-1/2 -translate-x-1/2"
                            : "right-1/2 translate-x-1/2"
                        } bottom-full mb-2 bg-white rounded-lg shadow-lg p-3 z-10 flex flex-wrap gap-2 justify-center`}
                      >
                        <button
                          onClick={() => shareOnSocialMedia("facebook")}
                          className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition"
                          title={translations.facebookShare}
                        >
                          <FontAwesomeIcon icon={faFacebookF} size="sm" />
                        </button>
                        <button
                          onClick={() => shareOnSocialMedia("twitter")}
                          className="w-10 h-10 rounded-full bg-blue-400 text-white flex items-center justify-center hover:bg-blue-500 transition"
                          title={translations.twitterShare}
                        >
                          <FontAwesomeIcon icon={faTwitter} size="sm" />
                        </button>
                        <button
                          onClick={() => shareOnSocialMedia("linkedin")}
                          className="w-10 h-10 rounded-full bg-blue-800 text-white flex items-center justify-center hover:bg-blue-900 transition"
                          title={translations.linkedinShare}
                        >
                          <FontAwesomeIcon icon={faLinkedinIn} size="sm" />
                        </button>
                        <button
                          onClick={() => shareOnSocialMedia("whatsapp")}
                          className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600 transition"
                          title={translations.whatsappShare}
                        >
                          <FontAwesomeIcon icon={faWhatsapp} size="sm" />
                        </button>
                        <button
                          onClick={copyLink}
                          className="w-10 h-10 rounded-full bg-gray-600 text-white flex items-center justify-center hover:bg-gray-700 transition"
                          title={translations.copyLink}
                        >
                          <FontAwesomeIcon icon={faLink} size="sm" />
                        </button>
                      </div>
                    )}
                  </div>

                  <Link
                    to="/news"
                    className={`bg-mauritania-green text-white py-2 px-6 rounded-md hover:bg-mauritania-green-dark transition duration-300 flex items-center ${
                      language === "ar" ? "flex-row" : "flex-row-reverse"
                    }`}
                  >
                    {language === "ar" ? (
                      <>
                        {translations.backToNews}
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        {translations.backToNews}
                      </>
                    )}
                  </Link>
                </div>
              </div>
            </article>

            {/* Related News */}
            {relatedNews.length > 0 && (
              <div className="mt-16">
                <h2
                  className={`text-2xl font-bold text-mauritania-green-dark mb-8 border-b pb-2 ${getTextAlignClass()}`}
                >
                  {translations.relatedNews}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedNews.map((related) => (
                    <div
                      key={related.id}
                      className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full"
                    >
                      <div className="relative h-0 pb-[56.25%] overflow-hidden">
                        <img
                          src={
                            resolveMediaUrl(related.image) ||
                            "https://images.unsplash.com/photo-1581093450020-7c01c5c81a2b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                          }
                          alt={getRelatedNewsTitle(related)}
                          className="absolute inset-0 w-full h-full object-cover transition duration-700 hover:scale-110"
                        />
                      </div>
                      <div className="p-4 flex flex-col flex-grow">
                        <div
                          className={`flex items-center text-sm text-mauritania-gold mb-2 ${getTextAlignClass()}`}
                        >
                          <FontAwesomeIcon
                            icon={faCalendarAlt}
                            className={language === "ar" ? "mr-2" : "ml-2"}
                          />
                          {related.datepublication
                            ? formatDate(related.datepublication)
                            : translations.undefinedDate}
                        </div>
                        <h3
                          className={`font-bold text-mauritania-green-dark mb-3 line-clamp-2 ${getTextAlignClass()}`}
                        >
                          {getRelatedNewsTitle(related)}
                        </h3>
                        <Link
                          to={`/news/${related.id}`}
                          className={`text-mauritania-green text-sm hover:text-mauritania-green-dark mt-auto flex items-center ${
                            language === "ar" ? "flex-row" : "flex-row-reverse"
                          }`}
                        >
                          {translations.readMore}
                          <FontAwesomeIcon
                            icon={faArrowRight}
                            className={
                              language === "ar"
                                ? "mr-2 transform rotate-180"
                                : "ml-2"
                            }
                          />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Same as in News component */}
          <div className="lg:w-1/4 space-y-6">
            {/* Search Bar */}

            {/* Last Announcements */}
            <div className="bg-white rounded-lg shadow-custom p-6">
              <div
                className={`flex items-center mb-4 pb-3 border-b border-gray-200 ${getTextAlignClass()}`}
              >
                <FontAwesomeIcon
                  icon={faBullhorn}
                  className="text-mauritania-gold text-xl mx-2"
                />
                <h3 className="text-lg font-bold text-mauritania-green-dark">
                  {translations.lastAnnouncements}
                </h3>
              </div>

              {announcementsLoading ? (
                <div className="text-center py-8">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="text-mauritania-green-dark text-2xl animate-spin"
                  />
                </div>
              ) : announcements.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">
                  {translations.noAnnouncements}
                </p>
              ) : (
                <div className="space-y-4">
                  {announcements.map((announcement) => (
                    <Link
                      key={announcement.id}
                      to={`/announcements/${announcement.id}`}
                      className="block pb-3 border-b border-gray-100 last:border-b-0 last:pb-0 hover:bg-gray-50 -mx-3 px-3 rounded transition-colors duration-200"
                    >
                      <div
                        className={`flex items-start ${
                          language === "ar" ? "space-x-reverse" : "space-x"
                        } space-x-3`}
                      >
                        <div className="flex-shrink-0 w-2 h-2 bg-mauritania-gold rounded-full mt-2"></div>
                        <div className="flex-1 min-w-0">
                          <h4
                            className={`text-sm font-semibold text-mauritania-green-dark line-clamp-2 mb-1 ${getTextAlignClass()} hover:text-mauritania-green transition-colors duration-200`}
                          >
                            {getAnnouncementTitle(announcement)}
                          </h4>
                          <div
                            className={`flex items-center text-xs text-gray-500 ${getTextAlignClass()}`}
                          >
                            <FontAwesomeIcon
                              icon={faCalendarAlt}
                              className={language === "ar" ? "ml-1" : "mr-1"}
                            />
                            {announcement.datepublication
                              ? formatDate(announcement.datepublication)
                              : translations.undefinedDate}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {announcements.length > 0 && (
                <Link
                  to="/announcements"
                  className={`block mt-4 text-center text-mauritania-green hover:text-mauritania-green-dark font-semibold text-sm ${
                    language === "ar" ? "flex-row" : "flex-row-reverse"
                  } flex items-center justify-center`}
                >
                  {translations.viewAllAnnouncements}
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className={
                      language === "ar" ? "mr-2" : "ml-2 transform rotate-180"
                    }
                  />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsDetail;
